import React, { useState, useEffect } from 'react';
import AIIntelligencePanel from './AIIntelligencePanel';
import {
  Activity, RefreshCw, Calendar, AlertTriangle, CheckCircle, Clock,
} from 'lucide-react';
import {
  emptyMsg, cardWrap, neuShadow, btnSm,
} from './crmShared';

/* ═══════════════════════════════════════════════════════════════════════════
   OperationsDashboardTab — clinic operations at a glance.
   • Today's appointment list with status pills
   • No-show rate over selected window
   • Treatment volume (completed appts per day)
   • Upcoming 7-day load (booked vs confirmed)
   ═══════════════════════════════════════════════════════════════════════════ */

const STATUS_COLORS = {
  scheduled: '#2E8B98',
  confirmed: '#62BB46',
  completed: '#15803D',
  cancelled: '#94A3B8',
  no_show: '#DC3545',
  rescheduled: '#F26522',
};

const STATUS_LABELS = {
  scheduled: 'Scheduled',
  confirmed: 'Confirmed',
  completed: 'Completed',
  cancelled: 'Cancelled',
  no_show: 'No-show',
  rescheduled: 'Rescheduled',
};

export default function OperationsDashboardTab() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  async function load(d = days) {
    setLoading(true);
    try {
      const r = await window.zignagenixAPI.getOperationsAnalytics({ days: d });
      setData(r);
    } catch (err) {
      console.error('Operations analytics failed', err);
    } finally {
      setLoading(false);
    }
  }

  // eslint-disable-next-line
  useEffect(() => { load(days); }, [days]);

  const peakUpcoming = Math.max(1, ...(data?.upcomingByDay?.map(d => d.count) || []));

  return (
    <>
      {/* Phase F.2: AI Intelligence panel — silent role gate */}
      <AIIntelligencePanel kind="operations" />

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Activity size={18} color="#2E8B98" />
          <h2 style={{ fontSize: 16, fontWeight: 700, margin: 0, color: '#212529' }}>Operations Dashboard</h2>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {[7, 30, 90].map(d => (
            <button key={d} onClick={() => setDays(d)} style={{
              padding: '6px 12px', borderRadius: 999, border: 'none',
              background: days === d ? '#2E8B98' : '#F1F5F9',
              color: days === d ? '#fff' : '#475569',
              fontSize: 11, fontWeight: 700, cursor: 'pointer',
            }}>{d}d</button>
          ))}
          <button onClick={() => load()} style={btnSm} title="Refresh">
            <RefreshCw size={12} /> Refresh
          </button>
        </div>
      </div>

      {/* Top KPIs */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: 20 }}>
        <KPICard icon={Calendar} color="#2E8B98" label="Today" value={data?.todayCount ?? '—'} sub="appointments" />
        <KPICard icon={CheckCircle} color="#15803D" label={`Treatments (${days}d)`} value={data?.totalCompletedInWindow ?? '—'} sub="completed" />
        <KPICard icon={AlertTriangle} color={data && data.noShowPct >= 15 ? '#DC3545' : '#B45309'}
          label="No-show rate" value={data ? `${data.noShowPct}%` : '—'}
          sub={data ? `${data.noShowCount} of ${data.completedCount + data.noShowCount}` : null} />
      </div>

      {/* Today's appointments */}
      <div style={{ ...cardWrap, padding: 14, marginBottom: 16 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: '#212529', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
          <Calendar size={14} color="#2E8B98" /> Today's Schedule
          {data?.todayCount > 0 && (
            <span style={{ fontSize: 11, fontWeight: 400, color: '#6C757D' }}>· {data.todayCount} appt{data.todayCount === 1 ? '' : 's'}</span>
          )}
        </div>
        {loading && !data ? (
          <div style={emptyMsg}>Loading…</div>
        ) : !data?.todayAppts?.length ? (
          <div style={emptyMsg}>No appointments today.</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {data.todayAppts.map(a => (
              <div key={a.id} style={{
                display: 'grid', gridTemplateColumns: '70px 1fr 1fr 100px',
                padding: '8px 10px',
                background: '#fff',
                borderRadius: 6,
                alignItems: 'center',
                gap: 8,
                fontSize: 12,
              }}>
                <div style={{ fontFamily: 'monospace', fontWeight: 700, color: '#2E8B98' }}>
                  {a.start_time ? new Date(a.start_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '—'}
                </div>
                <div style={{ fontWeight: 600, color: '#212529' }}>{a.title || '(untitled)'}</div>
                <div style={{ fontSize: 11, color: '#6C757D' }}>{a.location || '—'}</div>
                <div style={{
                  display: 'inline-flex', alignSelf: 'center', justifySelf: 'end',
                  padding: '3px 8px', borderRadius: 999, fontSize: 10, fontWeight: 700,
                  background: `${STATUS_COLORS[a.status] || '#94A3B8'}22`,
                  color: STATUS_COLORS[a.status] || '#475569',
                }}>
                  {STATUS_LABELS[a.status] || a.status || 'Unknown'}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Upcoming 7-day load */}
      <div style={{ ...cardWrap, padding: 14, marginBottom: 16 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: '#212529', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
          <Clock size={14} color="#F26522" /> Next 7 Days
        </div>
        {!data?.upcomingByDay?.length ? (
          <div style={emptyMsg}>Nothing on the schedule for the next week.</div>
        ) : (
          <div style={{ display: 'flex', gap: 6, alignItems: 'flex-end', height: 100 }}>
            {data.upcomingByDay.map((d, i) => {
              const heightPct = (d.count / peakUpcoming) * 100;
              const date = new Date(d.day);
              return (
                <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                  <div style={{ fontSize: 10, color: '#6C757D', fontWeight: 600 }}>{d.count}</div>
                  <div style={{
                    width: '100%', height: `${heightPct}%`, minHeight: 2,
                    background: '#2E8B98', borderRadius: '4px 4px 0 0',
                    position: 'relative',
                  }}>
                    {d.confirmed > 0 && (
                      <div style={{
                        position: 'absolute', bottom: 0, left: 0, right: 0,
                        height: `${(d.confirmed / d.count) * 100}%`,
                        background: '#62BB46',
                        borderRadius: '0 0 4px 4px',
                      }} title={`${d.confirmed} confirmed`} />
                    )}
                  </div>
                  <div style={{ fontSize: 9, color: '#94A3B8' }}>
                    {date.toLocaleDateString(undefined, { weekday: 'short' })}
                  </div>
                  <div style={{ fontSize: 9, color: '#475569', fontWeight: 600 }}>
                    {date.toLocaleDateString(undefined, { month: 'numeric', day: 'numeric' })}
                  </div>
                </div>
              );
            })}
          </div>
        )}
        <div style={{ display: 'flex', gap: 12, justifyContent: 'center', marginTop: 8, fontSize: 10, color: '#6C757D' }}>
          <span><span style={{ display: 'inline-block', width: 8, height: 8, background: '#62BB46', borderRadius: 2, marginRight: 4 }} />Confirmed</span>
          <span><span style={{ display: 'inline-block', width: 8, height: 8, background: '#2E8B98', borderRadius: 2, marginRight: 4 }} />Booked, not yet confirmed</span>
        </div>
      </div>

      {/* Treatment volume trend */}
      {data?.treatmentVolume?.length > 0 && (
        <div style={{ ...cardWrap, padding: 14 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#212529', marginBottom: 10 }}>
            Treatment Volume — last {days} days
          </div>
          <Sparkline points={data.treatmentVolume} color="#15803D" />
        </div>
      )}

      <div style={{ fontSize: 10, color: '#94A3B8', marginTop: 10, textAlign: 'center' }}>
        No-show rate counts only completed and no-show statuses (cancelled / rescheduled excluded).
        Treatment volume = appointments with status='completed'.
      </div>
    </>
  );
}

/* ─── KPI card ─────────────────────────────────────────────────────────────── */

function KPICard({ icon: Icon, color, label, value, sub }) {
  return (
    <div style={{ background: '#F8F9FA', borderRadius: 14, boxShadow: neuShadow, padding: '14px 16px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
        <Icon size={13} color={color} />
        <span style={{ fontSize: 10, color: '#6C757D', fontWeight: 700, textTransform: 'uppercase' }}>{label}</span>
      </div>
      <div style={{ fontSize: 22, fontWeight: 800, color }}>{value}</div>
      {sub && <div style={{ fontSize: 10, color: '#6C757D', marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

/* ─── Sparkline ───────────────────────────────────────────────────────────── */

function Sparkline({ points, color = '#2E8B98' }) {
  if (!points || points.length === 0) return null;
  const w = 600, h = 60, pad = 4;
  const max = Math.max(...points.map(p => p.count), 1);
  const stepX = (w - pad * 2) / Math.max(1, points.length - 1);
  const path = points.map((p, i) => {
    const x = pad + i * stepX;
    const y = h - pad - (p.count / max) * (h - pad * 2);
    return `${i === 0 ? 'M' : 'L'} ${x.toFixed(1)} ${y.toFixed(1)}`;
  }).join(' ');
  return (
    <div style={{ overflow: 'auto' }}>
      <svg width="100%" height={h} viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none">
        <path d={path} fill="none" stroke={color} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
        {points.map((p, i) => {
          const x = pad + i * stepX;
          const y = h - pad - (p.count / max) * (h - pad * 2);
          return <circle key={i} cx={x} cy={y} r="2" fill={color} />;
        })}
      </svg>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 9, color: '#94A3B8', marginTop: 4 }}>
        <span>{points[0]?.day ? new Date(points[0].day).toLocaleDateString() : ''}</span>
        <span>peak: {max}/day</span>
        <span>{points[points.length - 1]?.day ? new Date(points[points.length - 1].day).toLocaleDateString() : ''}</span>
      </div>
    </div>
  );
}
